import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const getAdviser = (grade, section) => {
    if (grade === 'Grade 7') {
        if (section && section.toLowerCase() === 'bamboo') return 'Zyrele Angelie G. Hinampas';
        if (section && section.toLowerCase() === 'coconut') return 'Jiean-Mae Y. Tamayo';
        return 'Zyrele Angelie G. Hinampas';
    }
    if (grade === 'Grade 8') return 'Lellan Joy T. Balansag';
    if (grade === 'Grade 9') return 'Aleakim B. Kenis';
    if (grade === 'Grade 10') return 'Via B. Carson';
    if (grade === 'Grade 11') return 'Jayson W. Calumbay';
    if (grade === 'Grade 12') return 'Cindy R. Estillore';
    return 'Lellan Joy T. Balansag';
};

const Finance = ({ students }) => {
    // --- SMART SEARCH & SELECTION STATE ---
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const searchRef = useRef(null);

    // --- TRANSACTION FORM STATE ---
    const [feeCategory, setFeeCategory] = useState('Tuition Fee');
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [hasSubsidy, setHasSubsidy] = useState(false);

    // --- LEDGER & PRINT STATE ---
    const [transactions, setTransactions] = useState([]);
    const [receiptToPrint, setReceiptToPrint] = useState(null);
    const [soaToPrint, setSoaToPrint] = useState(null); // Now stores an array for both single and bulk
    const [isBulkSoaPrint, setIsBulkSoaPrint] = useState(false); // Flag to toggle template
    const [clearanceToPrint, setClearanceToPrint] = useState(null);
    const [permitToPrint, setPermitToPrint] = useState(null);
    const [noticeToPrint, setNoticeToPrint] = useState(null);
    const [clearanceQuarter, setClearanceQuarter] = useState('1st');
    const [bulkClearanceGrade, setBulkClearanceGrade] = useState('All');
    const [bulkClearanceIndividual, setBulkClearanceIndividual] = useState('');

    // --- BULK SOA STATE ---
    const [bulkSoaGrade, setBulkSoaGrade] = useState('');
    const [bulkSoaSection, setBulkSoaSection] = useState('');
    const [isPreparingBulkSoa, setIsPreparingBulkSoa] = useState(false);

    // Derived unique grades and sections
    const uniqueGrades = [...new Set(students?.map(s => s.grade_level))].filter(Boolean).sort();
    const availableSections = [...new Set(students?.filter(s => s.grade_level === bulkSoaGrade).map(s => s.section))].filter(Boolean).sort();

    useEffect(() => {
        if (!bulkSoaGrade && uniqueGrades.length > 0) {
            setBulkSoaGrade(uniqueGrades[0]);
        }
    }, [students, bulkSoaGrade, uniqueGrades]);

    useEffect(() => {
        if (availableSections.length > 0 && !availableSections.includes(bulkSoaSection)) {
            setBulkSoaSection(availableSections[0]);
        }
    }, [bulkSoaGrade, availableSections, bulkSoaSection]);

    // Filter students for the search dropdown
    const filteredStudents = students?.filter(s => {
        const fullName = `${s.first_name} ${s.last_name}`.toLowerCase();
        const lrn = (s.lrn || '').toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || lrn.includes(searchTerm.toLowerCase());
    }) || [];

    // --- 1. THE GET BRIDGE: Fetch History on Select ---
    const handleSelectStudent = async (student) => {
        try {
            const response = await axios.get(`/api/payments/${student.lrn}`);
            const fetchedHistory = response.data;

            let calculatedTotalPaid = 0;
            const formattedHistory = fetchedHistory.map(tx => {
                const isPOS = tx.remarks.includes('[POS Transaction]');
                if (!isPOS) {
                    calculatedTotalPaid += parseFloat(tx.amount);
                }
                return {
                    date: new Date(tx.payment_date).toLocaleString(),
                    amount: parseFloat(tx.amount),
                    remarks: tx.remarks
                };
            });

            const isSHS = student.grade_level?.includes('11') || student.grade_level?.includes('12');
            const defaultAssessment = isSHS ? 18600.00 : 13100.00;
            const studentHasSubsidy = student.subsidy_status === 'ESC' || student.subsidy_status === 'QVR';
            const subsidyAmt = studentHasSubsidy ? (isSHS ? 14500 : 9000) : 0;
            const arrears = parseFloat(student.previous_balance || 0);
            const baseAssessment = parseFloat(student.assessment || defaultAssessment);

            const studentWithFinance = {
                ...student,
                assessment: baseAssessment,
                previous_balance: arrears,
                totalPaid: calculatedTotalPaid,
                history: formattedHistory,
                balance: (baseAssessment - subsidyAmt + arrears) - calculatedTotalPaid
            };

            setHasSubsidy(studentHasSubsidy);
            setSelectedStudent(studentWithFinance);
            setSearchTerm(`${student.last_name}, ${student.first_name}`);
            setIsSearching(false);

        } catch (error) {
            console.error("Failed to fetch payment history:", error);
            // If server fails, load them anyway with 0 balance so we don't break the UI
            const isSHS = student.grade_level?.includes('11') || student.grade_level?.includes('12');
            const defaultAssessment = isSHS ? 18600.00 : 13100.00;
            const studentHasSubsidy = student.subsidy_status === 'ESC' || student.subsidy_status === 'QVR';
            const subsidyAmt = studentHasSubsidy ? (isSHS ? 14500 : 9000) : 0;
            const arrears = parseFloat(student.previous_balance || 0);
            setHasSubsidy(studentHasSubsidy);
            setSelectedStudent({
                ...student,
                assessment: defaultAssessment,
                previous_balance: arrears,
                totalPaid: 0,
                balance: (defaultAssessment - subsidyAmt + arrears),
                history: []
            });
            setSearchTerm(`${student.last_name}, ${student.first_name}`);
            setIsSearching(false);
        }
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) setIsSearching(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggleSubsidy = async (e) => {
        const checked = e.target.checked;
        setHasSubsidy(checked);

        if (selectedStudent) {
            const isSHS = selectedStudent.grade_level?.includes('11') || selectedStudent.grade_level?.includes('12');
            const newStatus = checked ? (isSHS ? 'QVR' : 'ESC') : 'Non-ESC / Non-QVR';
            
            const updatedStudent = { ...selectedStudent, subsidy_status: newStatus };
            setSelectedStudent(updatedStudent);
            
            try {
                await axios.put(`/api/students/${selectedStudent.lrn}`, updatedStudent);
            } catch (error) {
                console.error("Failed to save subsidy status", error);
            }
        }
    };

    // --- 2. THE POST BRIDGE: Save New Payment ---
    const handleAddTransaction = async (e) => {
        e.preventDefault();
        if (!selectedStudent || !amount) return alert("Please select a student and enter an amount.");

        const parsedAmount = parseFloat(amount);
        const isAssessmentFee = ['Tuition Fee', 'Misc Fee', 'PTA Contribution'].includes(feeCategory);
        const combinedRemarks = `${feeCategory} (${paymentMethod})${!isAssessmentFee ? ' - [POS Transaction]' : ''} - Cashier 1`;

        const paymentPayload = {
            lrn: selectedStudent.lrn,
            amount: parsedAmount,
            payment_date: new Date().toISOString().slice(0, 19).replace('T', ' '),
            remarks: combinedRemarks
        };

        try {
            const response = await axios.post('/api/payments', paymentPayload);

            if (response.data.success) {
                const newTx = {
                    id: Date.now(),
                    date: new Date().toLocaleString(),
                    student: selectedStudent,
                    category: feeCategory,
                    amount: parsedAmount,
                    method: paymentMethod,
                    cashier: 'Cashier 1'
                };

                setTransactions([newTx, ...transactions]);

                const updatedStudent = {
                    ...selectedStudent,
                    totalPaid: isAssessmentFee ? selectedStudent.totalPaid + parsedAmount : selectedStudent.totalPaid,
                    balance: isAssessmentFee ? selectedStudent.balance - parsedAmount : selectedStudent.balance,
                    history: [{ date: newTx.date, amount: parsedAmount, remarks: combinedRemarks }, ...selectedStudent.history]
                };

                setSelectedStudent(updatedStudent);
                setAmount('');
                setFeeCategory('Tuition Fee');
            } else {
                alert("Server responded, but payment may not have saved.");
            }
        } catch (error) {
            console.error("Database Save Error:", error);
            alert("❌ Failed to save payment to the database.");
        }
    };

    // --- 3. PRINT FUNCTIONS ---
    const handlePrintReceipt = (tx) => {
        setSoaToPrint(null);
        setClearanceToPrint(null);
        setReceiptToPrint(tx);
        setTimeout(() => window.print(), 100);
    };

    const handlePrintSOA = () => {
        setReceiptToPrint(null);
        setClearanceToPrint(null);
        setIsBulkSoaPrint(false); // Single full page
        setSoaToPrint([{ ...selectedStudent, hasSubsidy }]); // Wrap in array for generic template
        setTimeout(() => window.print(), 100);
    };

    const handlePrintBulkSOA = async () => {
        if (!bulkSoaGrade || !bulkSoaSection) return alert("Please select a Grade Level and Section.");
        
        setIsPreparingBulkSoa(true);
        setReceiptToPrint(null);
        setClearanceToPrint(null);
        setIsBulkSoaPrint(true); // Bulk 4-per-page

        const targetStudents = students.filter(s => s.grade_level === bulkSoaGrade && s.section === bulkSoaSection);
        
        if (targetStudents.length === 0) {
            setIsPreparingBulkSoa(false);
            return alert("No students found in this section.");
        }

        const bulkSoaData = [];
        
        // Process sequentially to not overwhelm server
        for (const student of targetStudents) {
            try {
                const response = await axios.get(`/api/payments/${student.lrn}`);
                const fetchedHistory = response.data;

                let calculatedTotalPaid = 0;
                const formattedHistory = fetchedHistory.map(tx => {
                    const isPOS = tx.remarks.includes('[POS Transaction]');
                    if (!isPOS) {
                        calculatedTotalPaid += parseFloat(tx.amount);
                    }
                    return {
                        date: new Date(tx.payment_date).toLocaleString(),
                        amount: parseFloat(tx.amount),
                        remarks: tx.remarks
                    };
                });

                const isSHS = student.grade_level?.includes('11') || student.grade_level?.includes('12');
                const defaultAssessment = isSHS ? 18600.00 : 13100.00;
                const hasSubsidy = student.subsidy_status === 'ESC' || student.subsidy_status === 'QVR';

                bulkSoaData.push({
                    ...student,
                    assessment: student.assessment || defaultAssessment,
                    totalPaid: calculatedTotalPaid,
                    history: formattedHistory,
                    hasSubsidy: hasSubsidy
                });
            } catch (error) {
                console.error(`Failed to fetch history for ${student.lrn}`, error);
            }
        }

        // Sort alphabetically
        bulkSoaData.sort((a, b) => {
            const nameA = `${a.last_name}, ${a.first_name}`.toLowerCase();
            const nameB = `${b.last_name}, ${b.first_name}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });

        setSoaToPrint(bulkSoaData);
        setIsPreparingBulkSoa(false);
        setTimeout(() => window.print(), 100);
    };

    const handlePrintClearance = () => {
        setReceiptToPrint(null);
        setSoaToPrint(null);
        setPermitToPrint(null);
        setNoticeToPrint(null);
        setClearanceToPrint([selectedStudent]);
        setTimeout(() => window.print(), 100);
    };

    const handlePrintPermit = () => {
        setReceiptToPrint(null);
        setSoaToPrint(null);
        setClearanceToPrint(null);
        setNoticeToPrint(null);
        setPermitToPrint([selectedStudent]);
        setTimeout(() => window.print(), 100);
    };

    const handlePrintNotice = () => {
        setReceiptToPrint(null);
        setSoaToPrint(null);
        setClearanceToPrint(null);
        setPermitToPrint(null);
        setNoticeToPrint([selectedStudent]);
        setTimeout(() => window.print(), 100);
    };

    const handlePrintBulkForms = (formType) => {
        setReceiptToPrint(null);
        setSoaToPrint(null);
        setClearanceToPrint(null);
        setPermitToPrint(null);
        setNoticeToPrint(null);
        
        let activeStudents = (students || []).filter(s => s.status !== 'Graduated' && s.grade_level !== 'Alumni');

        if (bulkClearanceGrade !== 'All') {
            activeStudents = activeStudents.filter(s => s.grade_level === bulkClearanceGrade);
        }
        
        if (bulkClearanceIndividual.trim() !== '') {
            const search = bulkClearanceIndividual.toLowerCase();
            activeStudents = activeStudents.filter(s => 
                `${s.first_name} ${s.last_name}`.toLowerCase().includes(search)
            );
        }

        if (activeStudents.length > 0) {
            const sortedStudents = [...activeStudents].sort((a, b) => {
                const gradeA = a.grade_level || '';
                const gradeB = b.grade_level || '';
                if (gradeA === gradeB) {
                    const nameA = `${a.last_name}, ${a.first_name}`.toLowerCase();
                    const nameB = `${b.last_name}, ${b.first_name}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                }
                return gradeA.localeCompare(gradeB);
            });
            if (formType === 'clearance') setClearanceToPrint(sortedStudents);
            else if (formType === 'permit') setPermitToPrint(sortedStudents);
            else if (formType === 'notice') setNoticeToPrint(sortedStudents);
            
            setTimeout(() => window.print(), 100);
        } else {
            alert("No students available for bulk printing.");
        }
    };

    const dailyTotal = transactions.reduce((sum, tx) => sum + tx.amount, 0);

    const isSelectedSHS = selectedStudent?.grade_level?.includes('11') || selectedStudent?.grade_level?.includes('12');
    const subsidyName = isSelectedSHS ? "SHS Voucher Recipient" : "ESC Grantee";
    const subsidyAmount = isSelectedSHS ? 14500 : 9000;
    const activeSubsidy = hasSubsidy ? subsidyAmount : 0;

    return (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>

            {/* --- CONTROL PANEL (HIDDEN ON PRINT) --- */}
            <div className="no-print">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', paddingBottom: '20px', marginBottom: '25px' }}>
                    <div>
                        <h2 style={{ color: '#10b981', margin: '0 0 5px 0' }}>💳 Cashier & Collections Engine</h2>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Process payments and generate Official Receipts.</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>Today's Collection</p>
                        <h2 style={{ margin: 0, color: '#10b981', fontSize: '28px' }}>₱ {dailyTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h2>
                    </div>
                </div>

                {/* BULK ACTIONS / CLEARANCE & SOA */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
                    
                    {/* BATCH SOA PRINTING */}
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: '#f0fdf4', padding: '15px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                        <div>
                            <h3 style={{ margin: '0 0 5px 0', color: '#166534', fontSize: '16px' }}>🖨️ Batch SOA Printing</h3>
                            <p style={{ margin: '0 0 10px 0', color: '#15803d', fontSize: '12px' }}>Generate itemized Statements of Account by Section.</p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <select
                                value={bulkSoaGrade}
                                onChange={(e) => setBulkSoaGrade(e.target.value)}
                                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #4ade80', outline: 'none', fontWeight: 'bold', color: '#166534', backgroundColor: '#dcfce7', flex: 1 }}
                            >
                                <option value="" disabled>Select Grade</option>
                                {uniqueGrades.map(grade => <option key={grade} value={grade}>{grade}</option>)}
                            </select>
                            
                            <select
                                value={bulkSoaSection}
                                onChange={(e) => setBulkSoaSection(e.target.value)}
                                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #4ade80', outline: 'none', fontWeight: 'bold', color: '#166534', backgroundColor: '#dcfce7', flex: 1 }}
                            >
                                <option value="" disabled>Select Section</option>
                                {availableSections.map(sec => <option key={sec} value={sec}>{sec}</option>)}
                            </select>

                            <button onClick={handlePrintBulkSOA} disabled={isPreparingBulkSoa} style={{ padding: '8px 15px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: isPreparingBulkSoa ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                                {isPreparingBulkSoa ? '⏳ Preparing...' : 'Print SOAs'}
                            </button>
                        </div>
                    </div>

                    {/* BATCH CLEARANCE PRINTING */}
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: '#fffbeb', padding: '15px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                        <div>
                            <h3 style={{ margin: '0 0 5px 0', color: '#d97706', fontSize: '16px' }}>📝 Batch Clearance Printing</h3>
                            <p style={{ margin: '0 0 10px 0', color: '#b45309', fontSize: '12px' }}>Generate half-page clearance forms for all active students.</p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <select
                                value={clearanceQuarter}
                                onChange={(e) => setClearanceQuarter(e.target.value)}
                                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #f59e0b', outline: 'none', fontWeight: 'bold', color: '#b45309', backgroundColor: '#fef3c7', flex: 1 }}
                            >
                                <option value="1st">1st Quarter</option>
                                <option value="2nd">2nd Quarter</option>
                                <option value="3rd">3rd Quarter</option>
                                <option value="4th">4th Quarter</option>
                            </select>
                            <select
                                value={bulkClearanceGrade}
                                onChange={(e) => setBulkClearanceGrade(e.target.value)}
                                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #f59e0b', outline: 'none', fontWeight: 'bold', color: '#b45309', backgroundColor: '#fef3c7', flex: 1 }}
                            >
                                <option value="All">All Grades</option>
                                {uniqueGrades.map(grade => <option key={grade} value={grade}>{grade}</option>)}
                            </select>
                            <input
                                type="text"
                                placeholder="Search Name..."
                                value={bulkClearanceIndividual}
                                onChange={(e) => setBulkClearanceIndividual(e.target.value)}
                                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #f59e0b', outline: 'none', color: '#b45309', backgroundColor: '#fef3c7', flex: 1 }}
                            />
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <button onClick={() => handlePrintBulkForms('clearance')} style={{ padding: '8px 10px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                                    🖨️ Clearance
                                </button>
                                <button onClick={() => handlePrintBulkForms('permit')} style={{ padding: '8px 10px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                                    🖨️ Permit
                                </button>
                                <button onClick={() => handlePrintBulkForms('notice')} style={{ padding: '8px 10px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                                    🖨️ Notice
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SMART SEARCH */}
                <div style={{ position: 'relative', marginBottom: '25px' }} ref={searchRef}>
                    <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Find Payee (Student)</label>
                    <input
                        type="text"
                        placeholder="Search by LRN or Name..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsSearching(true);
                            if (selectedStudent) setSelectedStudent(null);
                        }}
                        onFocus={() => setIsSearching(true)}
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #cbd5e1', fontSize: '16px', fontWeight: 'bold', boxSizing: 'border-box' }}
                    />
                    {isSearching && searchTerm.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', marginTop: '5px', maxHeight: '250px', overflowY: 'auto', zIndex: 50, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                            {filteredStudents.map(s => (
                                <div key={s.lrn} onClick={() => handleSelectStudent(s)} style={{ padding: '15px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                    <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{s.last_name}, {s.first_name}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>LRN: {s.lrn} | {s.grade_level}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ACTIVE STUDENT DASHBOARD */}
                {selectedStudent && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>

                        {/* Payment Form */}
                        <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>💰 Record Payment</h3>
                            <form onSubmit={handleAddTransaction} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Amount (₱)</label>
                                        <input type="number" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box', fontWeight: 'bold' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Category</label>
                                        <select value={feeCategory} onChange={(e) => setFeeCategory(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                                            <option value="Tuition Fee">Tuition Fee</option>
                                            <option value="Misc Fee">Misc Fee</option>
                                            <option value="PTA Contribution">PTA Contribution</option>
                                            <option value="Diploma Printing">Diploma Printing</option>
                                            <option value="Certificate Printing">Certificate Request</option>
                                            <option value="Other Fees">Other Fees</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Payment Method</label>
                                        <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                                            <option value="Cash">Cash</option>
                                            <option value="Bank Transfer">Bank Transfer</option>
                                            <option value="Check">Check</option>
                                        </select>
                                    </div>
                                </div>
                                <button type="submit" style={{ padding: '12px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginTop: '10px' }}>Save Payment</button>
                            </form>
                        </div>

                        {/* Financial Summary, SOA Print & History */}
                        <div style={{ backgroundColor: '#f0f9ff', padding: '20px', borderRadius: '10px', border: '1px solid #bae6fd', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', color: '#334155' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        Base Assessment:
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', backgroundColor: hasSubsidy ? '#dcfce7' : '#f1f5f9', padding: '4px 8px', borderRadius: '6px', border: hasSubsidy ? '1px solid #86efac' : '1px solid #cbd5e1', cursor: 'pointer', color: hasSubsidy ? '#166534' : '#64748b', fontWeight: 'bold', transition: 'all 0.2s' }} title={isSelectedSHS ? "SHS Voucher Program - ₱14,500 Subsidy" : "Educational Service Contracting (PEAC) - ₱9,000 Subsidy"}>
                                            <input type="checkbox" checked={hasSubsidy} onChange={handleToggleSubsidy} style={{ cursor: 'pointer', accentColor: '#16a34a' }} />
                                            {subsidyName}
                                        </label>
                                    </span>
                                    <span>₱{selectedStudent.assessment?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</span>
                                </div>
                                {hasSubsidy && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#059669', fontWeight: 'bold' }}>
                                        <span>Less: {isSelectedSHS ? "Voucher Subsidy" : "ESC Subsidy"}:</span>
                                        <span>- ₱{subsidyAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                )}
                                {parseFloat(selectedStudent.previous_balance || 0) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#ef4444', fontWeight: 'bold' }}>
                                        <span>Add: Previous Arrears:</span>
                                        <span>+ ₱{parseFloat(selectedStudent.previous_balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', color: '#16a34a' }}>
                                    <span>Total Paid:</span>
                                    <span>- ₱{selectedStudent.totalPaid?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: 'white', padding: '15px', border: '2px solid #3b82f6', borderRadius: '8px', fontWeight: 'bold', fontSize: '18px' }}>
                                    <span>Balance:</span>
                                    <span style={{ color: '#ef4444' }}>₱{(((selectedStudent.assessment - activeSubsidy) + parseFloat(selectedStudent.previous_balance || 0)) - selectedStudent.totalPaid).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <button onClick={handlePrintSOA} style={{ width: '100%', padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '15px' }}>
                                    🖨️ Print Statement of Account (SOA)
                                </button>

                                <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                                    <button onClick={handlePrintClearance} style={{ flex: 1, padding: '10px 5px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                                        🖨️ Clearance
                                    </button>
                                    <button onClick={handlePrintPermit} style={{ flex: 1, padding: '10px 5px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                                        🖨️ Exam Permit
                                    </button>
                                    <button onClick={handlePrintNotice} style={{ flex: 1, padding: '10px 5px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                                        🖨️ Notice
                                    </button>
                                </div>
                            </div>

                            {/* On-Screen Payment History Table */}
                            <div style={{ marginTop: '20px', borderTop: '2px solid #bae6fd', paddingTop: '15px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: '#334155', fontSize: '14px' }}>Recent Payments</h4>
                                <div style={{ maxHeight: '180px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f5f9' }}>
                                            <tr style={{ borderBottom: '1px solid #cbd5e1', color: '#64748b' }}>
                                                <th style={{ padding: '8px' }}>Date</th>
                                                <th style={{ padding: '8px' }}>Amount</th>
                                                <th style={{ padding: '8px' }}>Remarks</th>
                                                <th style={{ padding: '8px', textAlign: 'center' }}>Receipt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedStudent.history && selectedStudent.history.length > 0 ? (
                                                selectedStudent.history.map((hist, idx) => (
                                                    <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '8px' }}>{hist.date.split(',')[0]}</td>
                                                        <td style={{ padding: '8px', fontWeight: 'bold', color: '#16a34a' }}>₱{hist.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                        <td style={{ padding: '8px' }}>{hist.remarks}</td>
                                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                                            <button 
                                                                onClick={() => {
                                                                    handlePrintReceipt({
                                                                        date: hist.date,
                                                                        student: selectedStudent,
                                                                        amount: hist.amount,
                                                                        remarks: hist.remarks
                                                                    });
                                                                }} 
                                                                style={{ padding: '4px 8px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '10px' }}
                                                            >
                                                                🖨️ Print OR
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr><td colSpan="4" style={{ padding: '15px', textAlign: 'center', color: '#94a3b8' }}>No payments recorded.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* DAILY LEDGER TABLE */}
                <h3 style={{ color: '#334155', fontSize: '16px', marginBottom: '15px', borderTop: '2px solid #f1f5f9', paddingTop: '20px' }}>Today's Cashier Ledger</h3>
                <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px', minWidth: '800px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                                <th style={{ padding: '12px' }}>Ref ID</th>
                                <th style={{ padding: '12px' }}>Date</th>
                                <th style={{ padding: '12px' }}>Student Name</th>
                                <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                                <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.length === 0 ? (
                                <tr><td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No transactions recorded today.</td></tr>
                            ) : (
                                transactions.map((tx) => (
                                    <tr key={tx.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px', color: '#64748b' }}>TXN-{tx.id.toString().slice(-6)}</td>
                                        <td style={{ padding: '12px' }}>{tx.date}</td>
                                        <td style={{ padding: '12px', fontWeight: 'bold' }}>{tx.student.last_name}</td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#166534' }}>₱{tx.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button onClick={() => handlePrintReceipt(tx)} style={{ padding: '6px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Print OR</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- NARROW RECEIPT TEMPLATE --- */}
            {receiptToPrint && (
                <div className="receipt-paper" style={{ display: 'none', width: '3.5in', padding: '0.2in', fontFamily: '"Courier New", Courier, monospace', fontSize: '12px', color: 'black' }}>
                    <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                        <h3 style={{ margin: '0' }}>SAN HERMINIGILDO</h3>
                        <p style={{ margin: '0', fontSize: '10px' }}>P4 Alanib, Lantapan, Bukidnon</p>
                        <h2 style={{ margin: '0', borderTop: '1px dashed black', borderBottom: '1px dashed black', padding: '5px 0' }}>OFFICIAL RECEIPT</h2>
                    </div>
                    <p><strong>Date:</strong> {receiptToPrint.date}</p>
                    <p><strong>Name:</strong> {receiptToPrint.student.first_name} {receiptToPrint.student.last_name}</p>
                    <p><strong>Remarks:</strong> {receiptToPrint.remarks || `${receiptToPrint.category} (${receiptToPrint.method})`}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px dashed black', paddingTop: '10px' }}>
                        <span>TOTAL</span>
                        <span>₱ {receiptToPrint.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                </div>
            )}

            {/* --- FULL PAGE SOA TEMPLATE (SINGLE PRINT) --- */}
            {soaToPrint && soaToPrint.length > 0 && !isBulkSoaPrint && (
                <div className="soa-paper" style={{ display: 'none', backgroundColor: 'white' }}>
                    {soaToPrint.map((studentSoa, index) => (
                        <div key={studentSoa.lrn} style={{ padding: '40px', fontFamily: '"Times New Roman", Times, serif', pageBreakAfter: index === soaToPrint.length - 1 ? 'auto' : 'always' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '40px', borderBottom: '2px solid black', paddingBottom: '20px' }}>
                                <img src="/owl.png" alt="SHAIS Logo" style={{ width: '80px' }} />
                                <div style={{ textAlign: 'center', color: 'black' }}>
                                    <h2 style={{ margin: '0 0 5px 0', fontSize: '24px', textTransform: 'uppercase' }}>San Herminigildo Agro-Industrial School</h2>
                                    <p style={{ margin: '0', fontSize: '14px' }}>P4 Alanib, Lantapan, 8722 Bukidnon</p>
                                </div>
                            </div>

                            <h1 style={{ textAlign: 'center', fontSize: '22px', textTransform: 'uppercase', marginBottom: '30px', letterSpacing: '2px' }}>Statement of Account</h1>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', fontSize: '16px' }}>
                                <div>
                                    <p style={{ margin: '5px 0' }}><strong>Student Name:</strong> {studentSoa.last_name}, {studentSoa.first_name}</p>
                                    <p style={{ margin: '5px 0' }}><strong>LRN:</strong> {studentSoa.lrn}</p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <p style={{ margin: '5px 0' }}><strong>Grade & Section:</strong> {studentSoa.grade_level} - {studentSoa.section || 'N/A'}</p>
                                    <p style={{ margin: '5px 0' }}><strong>Date Printed:</strong> {new Date().toLocaleDateString()}</p>
                                </div>
                            </div>

                            {(() => {
                                const isSoaSHS = studentSoa.grade_level?.includes('11') || studentSoa.grade_level?.includes('12');
                                const soaSubsidyAmt = studentSoa.hasSubsidy ? (isSoaSHS ? 14500 : 9000) : 0;
                                const soaArrears = parseFloat(studentSoa.previous_balance || 0);
                                const soaAssessment = parseFloat(studentSoa.assessment || (isSoaSHS ? 18600 : 13100));
                                const soaPaid = parseFloat(studentSoa.totalPaid || 0);
                                const soaBalance = (soaAssessment - soaSubsidyAmt + soaArrears) - soaPaid;

                                return (
                                    <div style={{ border: '2px solid black', padding: '20px', marginBottom: '30px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '18px' }}>
                                            <span>Total Assessment for School Year:</span>
                                            <span style={{ color: 'black', fontWeight: 'bold' }}>₱ {soaAssessment.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                        {studentSoa.hasSubsidy && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '18px', fontWeight: 'bold' }}>
                                                <span>Less: {isSoaSHS ? "Voucher Subsidy" : "ESC Subsidy"}:</span>
                                                <span style={{ color: 'black' }}>- ₱ {soaSubsidyAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        )}
                                        {soaArrears > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '18px', fontWeight: 'bold' }}>
                                                <span>Add: Previous Arrears:</span>
                                                <span style={{ color: 'black' }}>+ ₱ {soaArrears.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '18px', color: '#333' }}>
                                            <span>Total Payments Made:</span>
                                            <span style={{ color: 'black' }}>- ₱ {soaPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '2px solid black', fontSize: '20px', fontWeight: 'bold' }}>
                                            <span>CURRENT OUTSTANDING BALANCE:</span>
                                            <span style={{ color: 'black' }}>₱ {soaBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>
                                );
                            })()}

                            <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>Payment History</h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid black' }}>
                                        <th style={{ padding: '8px' }}>Date</th>
                                        <th style={{ padding: '8px' }}>Remarks</th>
                                        <th style={{ padding: '8px', textAlign: 'right' }}>Amount Paid</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {studentSoa.history.length === 0 ? (
                                        <tr><td colSpan="3" style={{ padding: '15px', textAlign: 'center' }}>No payments recorded yet.</td></tr>
                                    ) : (
                                        studentSoa.history.map((hist, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #ccc' }}>
                                                <td style={{ padding: '8px' }}>{hist.date.split(',')[0]}</td>
                                                <td style={{ padding: '8px' }}>{hist.remarks}</td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>₱ {hist.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>

                            <div style={{ marginTop: '80px', textAlign: 'right' }}>
                                <p style={{ margin: '0', fontWeight: 'bold', textDecoration: 'underline' }}>Cashier / Finance Office</p>
                                <p style={{ margin: '0', fontSize: '12px' }}>Authorized Signature</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* --- 4-PER-PAGE SOA TEMPLATE (BULK PRINT) --- */}
            {soaToPrint && soaToPrint.length > 0 && isBulkSoaPrint && (
                <div className="soa-bulk-paper" style={{ display: 'none', backgroundColor: 'white' }}>
                    {Array.from({ length: Math.ceil(soaToPrint.length / 4) }, (v, i) => soaToPrint.slice(i * 4, i * 4 + 4)).map((chunk, pageIndex, allChunks) => (
                        <div key={pageIndex} style={{ width: '8.5in', height: '11in', boxSizing: 'border-box', padding: '0.4in', pageBreakAfter: pageIndex === allChunks.length - 1 ? 'auto' : 'always', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '0.3in', fontFamily: '"Times New Roman", Times, serif' }}>
                            {chunk.map((studentSoa) => (
                                <div key={studentSoa.lrn} style={{ border: '2px dashed #94a3b8', padding: '0.2in', height: '100%', boxSizing: 'border-box', position: 'relative', color: 'black' }}>
                                    {/* Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                                        <img src="/owl.png" alt="SHAIS Logo" style={{ width: '45px', marginRight: '10px' }} />
                                        <div style={{ textAlign: 'center' }}>
                                            <h3 style={{ margin: '0', fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>San Herminigildo Agro-Industrial School</h3>
                                            <p style={{ margin: '0', fontSize: '9px' }}>P4 Alanib, Lantapan, Bukidnon</p>
                                        </div>
                                    </div>
                                    
                                    <h4 style={{ textAlign: 'center', margin: '5px 0 10px 0', fontSize: '13px', letterSpacing: '1px', borderTop: '1px solid black', borderBottom: '1px solid black', padding: '3px 0' }}>STATEMENT OF ACCOUNT</h4>
                                    
                                    {/* Student Info */}
                                    <div style={{ fontSize: '11px', marginBottom: '10px' }}>
                                        <p style={{ margin: '2px 0' }}><strong>Name:</strong> {studentSoa.last_name}, {studentSoa.first_name}</p>
                                        <p style={{ margin: '2px 0' }}><strong>LRN:</strong> {studentSoa.lrn}</p>
                                        <p style={{ margin: '2px 0' }}><strong>Grade & Section:</strong> {studentSoa.grade_level} - {studentSoa.section || 'N/A'}</p>
                                    </div>
                                    
                                    {/* Financial Breakdown */}
                                    {(() => {
                                        const isSoaSHS = studentSoa.grade_level?.includes('11') || studentSoa.grade_level?.includes('12');
                                        const soaSubsidyAmt = studentSoa.hasSubsidy ? (isSoaSHS ? 14500 : 9000) : 0;
                                        const soaArrears = parseFloat(studentSoa.previous_balance || 0);
                                        const soaAssessment = parseFloat(studentSoa.assessment || (isSoaSHS ? 18600 : 13100));
                                        const soaPaid = parseFloat(studentSoa.totalPaid || 0);
                                        const soaBalance = (soaAssessment - soaSubsidyAmt + soaArrears) - soaPaid;

                                        return (
                                            <div style={{ border: '1px solid black', padding: '10px', fontSize: '11px', backgroundColor: '#f8fafc' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                    <span>Annual Assessment:</span>
                                                    <span style={{ color: 'black', fontWeight: 'bold' }}>₱ {soaAssessment.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                                {studentSoa.hasSubsidy && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontWeight: 'bold' }}>
                                                        <span>Less {isSoaSHS ? "Voucher" : "ESC"}:</span>
                                                        <span style={{ color: 'black' }}>- ₱ {soaSubsidyAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                    </div>
                                                )}
                                                {soaArrears > 0 && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontWeight: 'bold' }}>
                                                        <span>Add Arrears:</span>
                                                        <span style={{ color: 'black' }}>+ ₱ {soaArrears.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                    <span>Total Paid:</span>
                                                    <span style={{ color: 'black' }}>- ₱ {soaPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid black', fontWeight: 'bold', fontSize: '14px' }}>
                                                    <span>BALANCE:</span>
                                                    <span style={{ color: 'black' }}>₱ {soaBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Mini Payment History */}
                                    <div style={{ marginTop: '10px', fontSize: '10px' }}>
                                        <h5 style={{ margin: '0 0 5px 0', textDecoration: 'underline' }}>Recent Payments</h5>
                                        {studentSoa.history.length > 0 ? (
                                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                <tbody>
                                                    {studentSoa.history.slice(0, 3).map((hist, idx) => (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                            <td style={{ padding: '2px 0', color: '#475569' }}>{hist.date.split(',')[0]}</td>
                                                            <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 'bold' }}>₱ {hist.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                        </tr>
                                                    ))}
                                                    {studentSoa.history.length > 3 && (
                                                        <tr><td colSpan="2" style={{ padding: '2px 0', fontStyle: 'italic', color: '#64748b', fontSize: '9px' }}>...and {studentSoa.history.length - 3} more payments</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div style={{ fontStyle: 'italic', color: '#64748b' }}>No payment records found.</div>
                                        )}
                                    </div>
                                    
                                    {/* Signatory */}
                                    <div style={{ position: 'absolute', bottom: '15px', right: '15px', textAlign: 'right' }}>
                                        <p style={{ margin: '0', fontSize: '11px', fontWeight: 'bold', textDecoration: 'underline' }}>Cashier / Finance Office</p>
                                        <p style={{ margin: '0', fontSize: '9px', color: '#64748b' }}>Printed: {new Date().toLocaleDateString()}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {/* --- FULL PAGE CLEARANCE TEMPLATE (SUPPORTS BULK, 2 PER PAGE) --- */}
            {clearanceToPrint && clearanceToPrint.length > 0 && (
                <div className="clearance-print-container" style={{ display: 'none', backgroundColor: 'white' }}>
                    {clearanceToPrint.map((student, idx) => {
                        const isSHS = student.grade_level?.includes('11') || student.grade_level?.includes('12');
                        const romanGrade = (student.grade_level || '').replace('Grade 7', 'VII').replace('Grade 8', 'VIII').replace('Grade 9', 'IX').replace('Grade 10', 'X').replace('Grade 11', 'XI').replace('Grade 12', 'XII');
                        const displaySection = student.section ? ` - ${student.section}` : '';
                        let subjectColumn1 = ['Araling Panlipunan', 'English', 'Filipino', 'MAPEH'];
                        let subjectColumn2 = ['Mathematics', 'Esp/Values Educ', 'Science & Tech', 'T.L.E'];
                        if (student.grade_level?.includes('11')) {
                            subjectColumn1 = ['General Science', 'Effective Communication', 'Gen. Mathematics', 'Life & Career'];
                            subjectColumn2 = ['Mabisang Kom.', 'Pag-aaral ng Kas.', 'Electives'];
                        } else if (student.grade_level?.includes('12')) {
                            subjectColumn1 = ['Contemporary Arts', 'Physical Science', 'Introduction to Philosophy', 'P.E & Health'];
                            subjectColumn2 = ['Empowerment Tech.', 'Humanities 1: CW', 'Practical Research 2', 'Electives'];
                        }
                        
                        return (
                        <div key={idx} className="clearance-half-page" style={{ height: '4.9in', marginBottom: idx % 2 === 0 ? '0.6in' : '0', padding: '0.3in 0.4in', borderBottom: idx % 2 === 0 ? '1px dashed #94a3b8' : 'none', boxSizing: 'border-box', fontFamily: '"Times New Roman", Times, serif', position: 'relative', color: 'black' }}>

                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                                <img src="/owl.png" alt="SHAIS Logo" style={{ width: '55px', position: 'absolute', left: '0.4in' }} />
                                <div style={{ textAlign: 'center' }}>
                                    <h3 style={{ margin: '0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '16px', fontWeight: 'bold' }}>San Herminigildo Agro-Industrial School</h3>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}>Recognition Permit No. 139, s. 1970</p>
                                    <p style={{ margin: '0', fontSize: '11px' }}>P4 Alanib, Lantapan, 8722 Bukidnon</p>
                                </div>
                            </div>

                            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                                <h2 style={{ color: '#b91c1c', margin: '0', fontSize: '18px', fontWeight: 'bold', textDecoration: 'underline' }}>{clearanceQuarter} QUARTER CLEARANCE</h2>
                                <h4 style={{ color: '#ea580c', margin: '3px 0 0 0', letterSpacing: '2px', fontSize: '12px' }}>{isSHS ? 'SENIOR HIGH LEVEL' : 'JUNIOR HIGH LEVEL'}</h4>
                            </div>

                            {/* Student Info */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px', marginBottom: '10px', fontSize: '13px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <strong style={{ marginRight: '8px' }}>Name:</strong>
                                    <div style={{ flex: 1, borderBottom: '1px solid black', paddingBottom: '2px', paddingLeft: '5px', textTransform: 'uppercase' }}>
                                        {student.last_name}, {student.first_name}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <strong style={{ marginRight: '8px' }}>Grade & Section:</strong>
                                    <div style={{ flex: 1, borderBottom: '1px solid black', paddingBottom: '2px', paddingLeft: '5px', textTransform: 'uppercase' }}>
                                        {romanGrade}{displaySection}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px', marginBottom: '20px', fontSize: '13px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <strong style={{ marginRight: '8px' }}>Date:</strong>
                                    <div style={{ flex: 1, borderBottom: '1px solid black', paddingBottom: '2px', paddingLeft: '5px' }}>
                                        {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <strong style={{ marginRight: '8px' }}>Date of Exam:</strong>
                                    <div style={{ flex: 1, borderBottom: '1px solid black', paddingBottom: '2px' }}></div>
                                </div>
                            </div>

                            {/* Subjects & Signatures */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '20px' }}>
                                <div style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '10px', borderBottom: '2px solid black', paddingBottom: '4px', fontSize: '12px' }}>
                                        <span>SUBJECT</span>
                                        <span>TEACHER'S SIGNATURE</span>
                                    </div>
                                    {subjectColumn1.map(subject => (
                                        <div key={subject} style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '10px' }}>
                                            <span style={{ fontSize: '12px', width: '130px', fontWeight: 'bold' }}>{subject}</span>
                                            <div style={{ flex: 1, borderBottom: '1px solid black' }}></div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '10px', borderBottom: '2px solid black', paddingBottom: '4px', fontSize: '12px' }}>
                                        <span>SUBJECT</span>
                                        <span>TEACHER'S SIGNATURE</span>
                                    </div>
                                    {subjectColumn2.map(subject => (
                                        <div key={subject} style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '10px' }}>
                                            <span style={{ fontSize: '12px', width: '130px', fontWeight: 'bold' }}>{subject}</span>
                                            <div style={{ flex: 1, borderBottom: '1px solid black' }}></div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Other Signatories */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px 20px', marginTop: '15px' }}>
                                {[
                                    { name: 'Honey D. Magsacay', title: 'SSG Treasurer' },
                                    { name: 'Krisa Jean B. Yam-oc, LPT', title: 'CAT Adviser' },
                                    { name: getAdviser(student.grade_level, student.section), title: 'Class Adviser' },
                                    { name: 'Mary Grace D. Ranollo', title: 'Clinic In- Charge' },

                                    { name: 'Via B. Carson, LPT', title: 'Activity Coordinator' },
                                    { name: 'Dayana P. Calipay', title: 'Guidance Counselor' },
                                    { name: 'Liezl M. Benlot', title: 'School Librarian' },
                                    { name: 'Richelle T. Raganut', title: 'School Cashier' },

                                    { name: '', title: 'M&M Adviser' },
                                    { name: 'Divina T. Donsig, LPT', title: 'School Registrar' },
                                    { name: 'Andre L. Tan', title: 'Prefect of Student' },
                                    { name: 'ALyssa L. Tan, LPT', title: 'School Principal' },
                                ].map((signatory, sIdx) => (
                                    <div key={sIdx} style={{ textAlign: 'center' }}>
                                        <div style={{ borderBottom: '1px solid black', fontWeight: 'bold', paddingBottom: '2px', fontSize: '11px', height: '14px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                                            {signatory.name}
                                        </div>
                                        <div style={{ fontSize: '10px', marginTop: '3px' }}>{signatory.title}</div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ fontSize: '10px', fontStyle: 'italic', position: 'absolute', bottom: '15px', left: '40px', color: '#555' }}>
                                In case of lost, student will pay Php 50.00.
                            </div>

                            {/* Dotted cutting line between forms, but hide on the bottom one */}
                            {idx % 2 === 0 && (
                                <div style={{ borderBottom: '1px dashed #ccc', position: 'absolute', bottom: '-0.1in', left: 0, right: 0 }}></div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}

            {/* --- QUARTER PAGE EXAM PERMIT TEMPLATE (SUPPORTS BULK, 4 PER PAGE) --- */}
            {permitToPrint && permitToPrint.length > 0 && (
                <div className="permit-print-container" style={{ display: 'none', backgroundColor: 'white' }}>
                    {permitToPrint.map((student, idx) => {
                        const isSHS = student.grade_level?.includes('11') || student.grade_level?.includes('12');
                        let subjects = ['Araling Panlipunan', 'English', 'Filipino', 'Esp/Values Educ', 'Mathematics', 'MAPEH', 'Science & Tech', 'T.L.E'];
                        if (student.grade_level?.includes('11')) {
                            subjects = ['General Science', 'Effective Communication', 'Gen. Mathematics', 'Life & Career', 'Mabisang Kom.', 'Pag-aaral ng Kas.', 'Electives'];
                        } else if (student.grade_level?.includes('12')) {
                            subjects = ['Contemporary Arts', 'Physical Science', 'Introduction to Philosophy', 'P.E & Health', 'Empowerment Tech.', 'Humanities 1: CW', 'Practical Research 2', 'Electives'];
                        }
                        
                        return (
                        <div key={idx} className="permit-quarter-page" style={{ width: '50%', height: '4.8in', float: 'left', padding: '0.15in 0.25in', borderRight: idx % 2 === 0 ? '1px dashed #94a3b8' : 'none', borderBottom: '1px dashed #94a3b8', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', position: 'relative', color: 'black' }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url(/owl.png)', backgroundSize: '200px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: 0.1, zIndex: 0 }}></div>
                            
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                {/* Header */}
                                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                                    <h3 style={{ margin: '0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '13px', fontWeight: 'bold' }}>San Herminigildo Agro-Industrial School</h3>
                                    <h2 style={{ margin: '8px 0 3px 0', color: '#4caf50', fontSize: '18px', letterSpacing: '2px', fontWeight: 'bold' }}>EXAMINATION PERMIT</h2>
                                    <p style={{ margin: '0', color: '#4caf50', fontSize: '11px', fontWeight: 'bold' }}>SCHOOL YEAR 2026-2027</p>
                                    <p style={{ margin: '0', color: '#4caf50', fontSize: '11px', fontWeight: 'bold' }}>{isSHS ? 'SENIOR HIGH SCHOOL' : 'JUNIOR HIGH SCHOOL'}</p>
                                </div>

                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '2px solid black', marginBottom: '10px', backgroundColor: 'transparent' }}>
                                    <tbody>
                                        <tr>
                                            <td colSpan="2" style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '10px' }}>NAME: {student.last_name}, {student.first_name}</td>
                                            <td style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', width: '30%', textTransform: 'uppercase', textAlign: 'center', fontSize: '10px' }}>{student.grade_level.replace('Grade ', 'G')} - {student.section}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', textAlign: 'center', width: '40%', fontSize: '10px' }}>SUBJECT</td>
                                            <td style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', textAlign: 'center', width: '35%', fontSize: '10px' }}>TEACHER</td>
                                            <td style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', textAlign: 'center', width: '25%', fontSize: '10px' }}>REMARK</td>
                                        </tr>
                                        {subjects.map(subject => (
                                            <tr key={subject}>
                                                <td style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', fontSize: '10px' }}>{subject}</td>
                                                <td style={{ border: '1px solid black', padding: '4px' }}></td>
                                                <td style={{ border: '1px solid black', padding: '4px' }}></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', marginTop: '15px', fontSize: '12px' }}>
                                    <span style={{ marginRight: '10px' }}>ISSUED BY: </span>
                                    <div style={{ width: '150px', borderBottom: '1px solid black' }}></div>
                                </div>
                                <div style={{ fontSize: '10px', fontStyle: 'italic', marginTop: '10px' }}>
                                    In case of loss, student will pay Php 50.00.
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {/* --- QUARTER PAGE NOTICE OF ACCOUNTS TEMPLATE (SUPPORTS BULK, 4 PER PAGE) --- */}
            {noticeToPrint && noticeToPrint.length > 0 && (
                <div className="notice-print-container" style={{ display: 'none', backgroundColor: 'white' }}>
                    {noticeToPrint.map((student, idx) => (
                        <div key={idx} className="notice-quarter-page" style={{ width: '50%', height: '4.8in', float: 'left', padding: '0.15in 0.25in', borderRight: idx % 2 === 0 ? '1px dashed #94a3b8' : 'none', borderBottom: '1px dashed #94a3b8', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', color: 'black' }}>
                            <div style={{ padding: '15px', height: '100%', boxSizing: 'border-box', position: 'relative' }}>
                                
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '15px' }}>
                                    <img src="/owl.png" alt="SHAIS Logo" style={{ width: '40px' }} />
                                    <div style={{ textAlign: 'center' }}>
                                        <h3 style={{ margin: '0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px' }}>San Herminigildo Agro-Industrial School</h3>
                                        <p style={{ margin: '0', fontSize: '10px' }}>P4 Alanib, Lantapan, Bukidnon</p>
                                    </div>
                                </div>

                                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                                    <h2 style={{ margin: '0', fontSize: '18px', fontWeight: 'bold' }}>NOTICE OF ACCOUNTS</h2>
                                    <h2 style={{ margin: '3px 0 0 0', fontSize: '16px', fontWeight: 'bold' }}>SY 2026-2027</h2>
                                </div>

                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid black', fontSize: '12px', tableLayout: 'fixed', wordWrap: 'break-word' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px', width: '40%' }}>Name:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '11px' }}>{student.last_name}, {student.first_name}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>Grade:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '11px' }}>{student.grade_level} - {student.section}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>Date:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px', fontSize: '11px' }}>{new Date().toLocaleDateString()}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>Previous Accounts:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}></td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>Entrance:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}></td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>Tuition:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}></td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>P.E Uniform:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}></td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>School Uniform:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}></td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}>Total:</td>
                                            <td style={{ border: '1px solid black', padding: '4px 8px' }}></td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', fontSize: '12px', gap: '8px' }}>
                                    <span style={{ fontWeight: 'bold' }}>Prepared by:</span>
                                    <div style={{ borderBottom: '1px solid black', width: '120px' }}></div>
                                    <span style={{ fontWeight: 'bold' }}>School Cashier</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Finance;